import React, { useState, useCallback } from "react";
import HToggle from "src/hcomponents/HToggle";
import HPageContainer from "../../../hcomponents/HPageContainer";
import SettingsTabsNavigation from "./SettingsTabsNavigation";
import HHeader from "../../../hcomponents/HHeader";
import HColorInput from "src/hcomponents/HColorInput";
import { Upload, Image, Palette, Eye, Check } from "lucide-react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import HUpgradeButton from "src/hcomponents/HUpgradeButton";
import toastUtils from "../../../utils/toast";
import * as localService from "../../../services/localService";
import { FeatureAccess } from "../../../utils/featureAccessControl";

export default function Branding() {
  const [hex, setHex] = useState("#000000");
  const history = useHistory();
  const currentUser = localService.getUser();
  const featureFlags = useSelector(state => state.subscription?.featureFlags || {});
  const hasWhiteLabeling = !!featureFlags?.WHITE_LABELING;

  const goToUpgrade = useCallback(() => {
    history.push("/host/pricing?view=packages");
  }, [history]);

  const notifyUpgrade = useCallback(() => {
    toastUtils.info("White labeling is available on higher plans.", {
      action: {
        label: "View plans",
        onClick: goToUpgrade,
      },
    });
  }, [goToUpgrade]);

  const handleToggleClick = handler => {
    if (!hasWhiteLabeling) {
      notifyUpgrade();
      return;
    }
    if (typeof handler === "function") handler();
  };

  const isValidHex = /^#([0-9A-Fa-f]{6})$/.test(hex);

  return (
    <HPageContainer>
      <HHeader />
      <div className="overflow-auto">
        <SettingsTabsNavigation />
        <h2 className="text-xl font-semibold mx-3 text-gray-800 mb-4">Branding Settings</h2>
        <div className="bg-white mx-3 mb-3 p-6 rounded-xl border border-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Colors Section */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Colors</h2>

              <div className="mb-4 ">
                <div className="flex gap-1">
                  <Palette size={18} />
                  <label className="block text-sm font-medium text-gray-700 mb-1">Primary Color</label>
                </div>
                <HColorInput />
              </div>

              <div className="mb-4">
                <div className="flex gap-1">
                  <Palette size={18} />
                  <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Color</label>
                </div>
                <HColorInput />
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:gap-4">
                <button
                  className="flex items-center gap-1 px-4 py-2 text-sm font-medium bg-white border border-gray-300 rounded-md
                   sm:px-3 sm:py-1.5 sm:text-xs sm:gap-0.5"
                >
                  <Eye size={16} className="sm:w-4 sm:h-4" />
                  Preview
                </button>
                <button className="flex items-center gap-1 px-4 py-2 text-sm font-medium bg-black text-white rounded-md sm:px-3 sm:py-1.5 sm:text-xs sm:gap-0.5">
                  <Check size={16} className="sm:w-4 sm:h-4" />
                  Apply Changes
                </button>
              </div>
            </div>

            {/* Logos & Icons Section */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Logos & Icons</h2>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Brand Logo</label>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <div className="h-20 w-20 rounded-lg border-2 border-dashed border-gray-300 bg-gray-100 flex items-center justify-center text-gray-400">
                    <Image />
                  </div>
                  <button className="flex space-x-2 bg-white text-gray-700 border border-gray-300 px-4 py-2 rounded-md text-sm font-medium shadow-xs hover:shadow-md transition">
                    <Upload size={17} />
                    <span>Upload Logo</span>
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Recommended: 512x512px, PNG format</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Favicon</label>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <div className="h-10 w-10 rounded-lg border-2 border-dashed border-gray-300 bg-gray-100 flex items-center justify-center text-gray-400">
                    <Image />
                  </div>
                  <button className="flex space-x-2 bg-white text-gray-700 border border-gray-300 px-4 py-2 rounded-md text-sm font-medium shadow-xs hover:shadow-md transition">
                    <Upload size={17} />
                    <span>Upload Favicon</span>
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Recommended: 32x32px, ICO or PNG</p>
              </div>
            </div>
          </div>

          {/* White Label Section */}
          <div className="mt-10 border-t border-gray-300 pt-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">White-Label Options</h2>

            {!hasWhiteLabeling && FeatureAccess.canUpgradePlan(currentUser) && (
              <div className="mb-6 border border-slate-200 bg-slate-50 text-slate-800 px-4 py-3 rounded-lg flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <p className="text-sm">Personalize your portal with white labeling by upgrading your plan.</p>
                <HUpgradeButton size="sm" onClick={goToUpgrade} />
              </div>
            )}

            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-medium text-gray-800">Enable White-Label</p>
                  <p className="text-xs text-gray-500">Hide all SPACIA branding from your portal</p>
                </div>
                <HToggle disabled={!hasWhiteLabeling} onClick={() => handleToggleClick()} />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Custom Domain</label>
                <input
                  type="text"
                  placeholder="app.spaciacoworking.com"
                  className={`w-full p-2 rounded-md ${hasWhiteLabeling ? "bg-gray-100 text-black" : "bg-gray-100 text-gray-400 cursor-not-allowed"}`}
                  disabled={!hasWhiteLabeling}
                />
                <p className="text-xs text-gray-500 mt-1">Enter your domain to customize the URL of your portal</p>
              </div>

              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-medium text-gray-800">Remove Branding "Powered by SPACIA"</p>
                  <p className="text-xs text-gray-500">Remove footer branding text</p>
                </div>
                <HToggle disabled={!hasWhiteLabeling} onClick={() => handleToggleClick()} />
              </div>

              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-medium text-gray-800">Customize Login Screen</p>
                  <p className="text-xs text-gray-500">Apply your branding to the login page</p>
                </div>
                <HToggle disabled={!hasWhiteLabeling} onClick={() => handleToggleClick()} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </HPageContainer>
  );
}
